import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getPhysicianSchedules, 
  upsertSchedule, 
  deleteSchedule,
} from '../../services/schedulesService';
import SkeletonLoader from '../shared/SkeletonLoader';
import EmptyState from '../shared/EmptyState';
import ErrorMessage from '../shared/ErrorMessage';
import toast from 'react-hot-toast';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const hour = String(i).padStart(2, '0');
  return `${hour}:00:00`;
});

/**
 * ScheduleManagement Component
 * Allows physicians to manage their weekly schedules and availability overrides
 */
const ScheduleManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    day_of_week: 1,
    start_time: '09:00:00',
    end_time: '17:00:00',
    break_start_time: '',
    break_end_time: '',
    is_available: true,
  });

  // Fetch schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!user?.id) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await getPhysicianSchedules(user.id);

        if (fetchError) {
          throw fetchError;
        }

        // Create array with all 7 days, filling in existing schedules
        const allDays = DAYS_OF_WEEK.map(day => {
          const existing = data?.find(s => s.day_of_week === day.value);
          return existing || {
            day_of_week: day.value,
            day_label: day.label,
            is_available: false,
            start_time: null,
            end_time: null,
          };
        });

        setSchedules(allDays);
      } catch (err) {
        console.error('Error fetching schedules:', err);
        setError('Failed to load schedules. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [user]);

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      day_of_week: schedule.day_of_week,
      start_time: schedule.start_time || '09:00:00',
      end_time: schedule.end_time || '17:00:00',
      break_start_time: schedule.break_start_time || '',
      break_end_time: schedule.break_end_time || '',
      is_available: schedule.is_available !== false,
    });
    setEditModalOpen(true);
  };

  const handleAdd = () => {
    setEditingSchedule(null);
    setFormData({
      day_of_week: 1,
      start_time: '09:00:00',
      end_time: '17:00:00',
      break_start_time: '',
      break_end_time: '',
      is_available: true,
    });
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.start_time || !formData.end_time) {
      toast.error('Please set start and end times');
      return;
    }

    if (formData.end_time <= formData.start_time) {
      toast.error('End time must be after start time');
      return;
    }

    if ((formData.break_start_time && !formData.break_end_time) || 
        (!formData.break_start_time && formData.break_end_time)) {
      toast.error('Both break start and end times must be set, or leave both empty');
      return;
    }

    try {
      const scheduleData = {
        physician_id: user.id,
        tenant_id: user.tenant_id,
        day_of_week: formData.day_of_week,
        start_time: formData.start_time,
        end_time: formData.end_time,
        break_start_time: formData.break_start_time || null,
        break_end_time: formData.break_end_time || null,
        is_available: formData.is_available,
      };

      const { error: saveError } = await upsertSchedule(scheduleData);

      if (saveError) {
        throw saveError;
      }

      toast.success('Schedule saved successfully');
      setEditModalOpen(false);
      
      // Refresh schedules
      const { data } = await getPhysicianSchedules(user.id);
      const allDays = DAYS_OF_WEEK.map(day => {
        const existing = data?.find(s => s.day_of_week === day.value);
        return existing || {
          day_of_week: day.value,
          day_label: day.label,
          is_available: false,
          start_time: null,
          end_time: null,
        };
      });
      setSchedules(allDays);
    } catch (err) {
      console.error('Error saving schedule:', err);
      toast.error(err.message || 'Failed to save schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!scheduleId) {
      toast.error('No schedule to delete');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      const { error: deleteError } = await deleteSchedule(scheduleId);

      if (deleteError) {
        throw deleteError;
      }

      toast.success('Schedule deleted successfully');
      
      // Refresh schedules
      const { data } = await getPhysicianSchedules(user.id);
      const allDays = DAYS_OF_WEEK.map(day => {
        const existing = data?.find(s => s.day_of_week === day.value);
        return existing || {
          day_of_week: day.value,
          day_label: day.label,
          is_available: false,
          start_time: null,
          end_time: null,
        };
      });
      setSchedules(allDays);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      toast.error(err.message || 'Failed to delete schedule');
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Manage Schedule
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set your weekly working hours and availability
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ borderRadius: 2 }}
        >
          Add Schedule
        </Button>
      </Box>

      {loading ? (
        <SkeletonLoader variant="table" count={7} />
      ) : error ? (
        <ErrorMessage
          title="Unable to load schedules"
          message={error}
          actionLabel="Retry"
          onAction={() => navigate(0)}
          severity="error"
        />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Day</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Start Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>End Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Break</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => {
                const dayInfo = DAYS_OF_WEEK.find(d => d.value === schedule.day_of_week);
                return (
                  <TableRow key={schedule.day_of_week} hover>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {dayInfo?.label || `Day ${schedule.day_of_week}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {schedule.start_time ? formatTime(schedule.start_time) : '-'}
                    </TableCell>
                    <TableCell>
                      {schedule.end_time ? formatTime(schedule.end_time) : '-'}
                    </TableCell>
                    <TableCell>
                      {schedule.break_start_time && schedule.break_end_time
                        ? `${formatTime(schedule.break_start_time)} - ${formatTime(schedule.break_end_time)}`
                        : 'No break'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={schedule.is_available ? 'Available' : 'Unavailable'}
                        color={schedule.is_available ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(schedule)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      {schedule.id && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(schedule.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit/Add Schedule Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={formData.day_of_week}
                label="Day of Week"
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
              >
                {DAYS_OF_WEEK.map(day => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Start Time</InputLabel>
              <Select
                value={formData.start_time}
                label="Start Time"
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              >
                {TIME_SLOTS.map(time => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>End Time</InputLabel>
              <Select
                value={formData.end_time}
                label="End Time"
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              >
                {TIME_SLOTS.map(time => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }}>Optional: Break Time</Divider>

            <FormControl fullWidth>
              <InputLabel>Break Start Time</InputLabel>
              <Select
                value={formData.break_start_time}
                label="Break Start Time"
                onChange={(e) => setFormData({ ...formData, break_start_time: e.target.value })}
              >
                <MenuItem value="">No break</MenuItem>
                {TIME_SLOTS.map(time => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Break End Time</InputLabel>
              <Select
                value={formData.break_end_time}
                label="Break End Time"
                onChange={(e) => setFormData({ ...formData, break_end_time: e.target.value })}
                disabled={!formData.break_start_time}
              >
                <MenuItem value="">No break</MenuItem>
                {TIME_SLOTS.map(time => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Availability</InputLabel>
              <Select
                value={formData.is_available ? 'available' : 'unavailable'}
                label="Availability"
                onChange={(e) => setFormData({ ...formData, is_available: e.target.value === 'available' })}
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduleManagement;

